import {Controller} from "@hotwired/stimulus";
import {OverlayScrollbars} from 'overlayscrollbars';
import debounce from 'lodash/debounce';

/**
 * HorizontalScrollController replaces the browser's native scrollbar for horizontal scrolling.
 *
 * Its main features are:
 * - it is always visible (the native one hides after a while on mobile devices)
 * - when the user scrolls inside the container, the scroll bar is glued
 *   to the bottom edge of the viewport in each position, so the user can see at any moment
 *   that part of the container content is not visible
 *
 * Application:
 * - select an element to be horizontally scrollable
 * - enclose this element in a div element:
 *    %div{data: { controller: 'horizontal-scroll' }}
 * - add a detector to the end of the element, necessary for the scroll bar to work properly:
 *    %div{data: { horizontal_scroll_target: 'verticalDetector' }}
 *
 * So, for example:
 *  %div{data: { controller: 'horizontal-scroll'}}
 *    %table
 *      %thead
 *      %tbody
 *   %div{data: { horizontal_scroll_target: 'verticalDetector' }}
 *
 * There are three basic categories of variations for this controller.
 *
 *    1. Simple controller with one verticalDetector element
 *
 *    2. Dialog - also uses a simple controller with one verticalDetector element,
 *       but we have to detect that it is a dialog.
 *       Remember that dialog cannot have nested HorizontalScrollControllers!
 *
 *    3. Tab - since there is a common controller for all tabs in layout, each tab has its own verticalDetector.
 *       Therefore, we need to use active tab element in this controller.
 *
 * Used technologies:
 *
 * OverlayScrollbars library
 * https://kingsora.github.io/OverlayScrollbars/
 *
 * IntersectionObserver
 * https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 */

export default class extends Controller {
  /* instance of the OverlayScrollbars */
  osInstance;

  /* OverlayScrollbars scroll bar */
  osScrollBar;

  /* original scroll bar styles */
  originalScrollBarStyles = {};

  /* styles for sticky scroll bar */
  fixedScrollBarStyles = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: 0,
  }

  /* Vertical container element, parent of verticalDetector */
  verticalContainer = null;
  /* Vertical detector element */
  verticalDetector = null;

  /* Tab element used by this controller as a target.  */
  tabElement = null;
  /* Detect if this controller target is dialog */
  isDialog = false;



  connect() {
    super.connect();
    /* initialize all we need */
    this._initOS();

    /* Start with this.element, it can be overridden later */
    this.verticalContainer = this.element;

    this._parentIsDialog();

    /* restart all on resize event */
    this.resizeListener = this._reinit.bind(this);
    window.addEventListener("resize", debounce(this.resizeListener, 100));

    /* listen to the tab change */
    this.tabChangeListener = this._tabChanged.bind(this);
    window.addEventListener('tab:changed', this.tabChangeListener);

    /* listen to the dialog open event */
    this.dialogOpenedListener = this._dialogOpened.bind(this);
    window.addEventListener('dialog:loaded', this.dialogOpenedListener);
  }

  disconnect() {
    super.disconnect();
    this.observer && this.observer.disconnect();
    this.osInstance.destroy();
    window.removeEventListener("resize", this.resizeListener);
    window.removeEventListener('tab:changed', this.tabChangeListener);
    window.removeEventListener('dialog:loaded', this.dialogOpenedListener);

    /* This is how we handle the situation when the user
       clicks too quickly on secondary menu items.
       Once the controller is disconnected, we need to exit the loop
       that searches for scrollbar elements.
       Otherwise, it would continue on and eventually throw an error. */
    this._waitForScrollBarElements(true);
  }

  _parentIsDialog() {
    /* Check if the parent element of this controller is a dialog. */
    this.isDialog = this.element.parentNode.tagName.toLowerCase() === 'dialog';
  }

  _dialogOpened = (e) => {
    /* When dialog is opened, we need to reset its position */
    const {viewport} = this.osInstance.elements();
    viewport.scrollTo({left: 0});
  }

  _tabChanged = (e) => {
    /* When tab is changed, we need to use the tab element as this controller target */
    this.tabElement = e.detail.tab;

    /* reset scrollbar position between tab changes */
    const {viewport} = this.osInstance.elements();
    viewport.scrollTo({left: 0});

    this._waitForScrollBarElements();
  }

  _initOS() {
    /* create instance of the OverlayScrollbars */
    this.osInstance = OverlayScrollbars({
        target: this.element,
      }, {
        overflow: {
          x: 'scroll',
          y: 'auto',
        },
        showNativeOverlaidScrollbars: false,
        scrollbars: {
          theme: 'os-theme-dark',
          visibility: 'visible',
          autoHide: 'never',
        },
      }, {
        initialized: (el) => {
          /* we need to wait for the OverlayScrollbars to be initialized */
          this._waitForScrollBarElements();
        },
        updated: (instance, event) => {
          /* we need to wait for the OverlayScrollbars to be initialized */
          this._waitForScrollBarElements();
        },
        scroll: (instance, event) => {
          /* emit a custom event when the user scrolls horizontally,
             this event is used by other controllers,
             especially in TableStickyHeaderController */
          const {viewport} = this.osInstance.elements();
          const {scrollLeft} = viewport;
          const horizontalScrollEvent = new CustomEvent('horizontal-scroll', {
            bubbles: false,
            detail: {scrollLeft: scrollLeft, container: this.verticalContainer},
          });
          window.dispatchEvent(horizontalScrollEvent);
        },
      }
    );
  }

  /* reinitialize after window resize */
  _reinit() {
    this.observer && this.observer.disconnect();
    this._initOS();
  }

  /* Some elements are generated asynchronously,
     so we have to wait for them to load. */
  _waitForScrollBarElements = (killCheckExists = true) => {
    let progressTime = 0;
    const timeout = 100;
    const maxTimeout = 5000;

    let osScrollBar;
    let verticalDetector;

    /* A more elegant solution would be using MutationObserver,
       but for some reason that doesn't work,
       so, I chose a different solution. */
    const checkExist = setInterval(() => {
      /* See comment in disconnect method. */
      killCheckExists && clearInterval(checkExist);

      /* this controller can have another nested HorizontalScrollControllers,
         for example inside tabs or dialogs, and they have its own
         verticalContainer and osScrollBar, so we need find direct children
         for this controller to get right corresponding elements */

      const verticalDetectorWrapper = this._findDirectChild(this.verticalContainer, '[data-overlayscrollbars-viewport]');
      if (verticalDetectorWrapper) {
        osScrollBar = this._findDirectChild(this.verticalContainer, '.os-scrollbar-horizontal');
        verticalDetector = this._findDirectChild(verticalDetectorWrapper, '[data-horizontal-scroll-target="verticalDetector"]');
      }

      /* if target of this controller is tab,
         we need to find vertical detector inside this tab.
         Again, tab can have another nested HorizontalScrollControllers,
         so we need find direct children */
      if (this.tabElement) {
        verticalDetector = this._findDirectChild(this.tabElement, '[data-horizontal-scroll-target="verticalDetector"]');
      }

      /* If target of this controller is dialog,
         we cannot look for direct children,  because
         we cannot wrap dialog element with HorizontalScrollController,
         so dialog cannot have nested HorizontalScrollController
         and we can use simple querySelector */
      if (this.isDialog) {
        osScrollBar = this.verticalContainer.querySelector('.os-scrollbar-horizontal');
        verticalDetector = this.verticalContainer.querySelector('[data-horizontal-scroll-target="verticalDetector"]');
      }

      /* If we have found all elements, we can stop timeout loop */
      if (verticalDetector && this.verticalContainer && osScrollBar) {
        this.osScrollBar = osScrollBar;
        this.verticalDetector = verticalDetector;
        clearInterval(checkExist);
        this._constructObserver();
      } else {
        /* If some elements are not found, and we have exceeded maxTimeout,
           we will terminate the loop with reporting an error. */
        progressTime += timeout;
        if (progressTime > maxTimeout) {
          clearInterval(checkExist);
          console.error(`HorizontalScrollController: Scrollbar elements not found after ${maxTimeout} milliseconds, check your layout structure.`);
        }
      }
    }, timeout);
  }

  _constructObserver() {
    /* create an instance of the IntersectionObserver
       and watch changes */
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (this._isVerticalDetector(entry) && entry.isIntersecting) {
            this._verticalDetectorEnteredViewport();
          } else if (this._isVerticalDetector(entry) && !entry.isIntersecting) {
            this._verticalDetectorLeftViewport();
          }
        });
      },
    );
    this.observer.observe(this.verticalDetector);
  }

  _isVerticalDetector(entry) {
    /* check if entry is a vertical detector */
    return entry.target.dataset.horizontalScrollTarget === 'verticalDetector'
  }

  _verticalDetectorLeftViewport() {
    /* when the vertical detector exits the viewport,
       we need to fix the scroll bar to the bottom of the viewport */
    /* remove the detection class from the scroll bar
       so scrollbar isn't forced to have min-width */
    this.osScrollBar.classList.remove('osh-absolute');
    this._calculateScrollBarStyles('left');
    Object.assign(this.osScrollBar.style, this.fixedScrollBarStyles);
  }

  _verticalDetectorEnteredViewport() {
    /* when the vertical detector enters the viewport,
       we need to restore the original scroll bar styles */
    /* add the detection class to the scroll bar
       so scrollbar is forced to have min-width 100% */
    this.osScrollBar.classList.add('osh-absolute');
    this._calculateScrollBarStyles('entered');
    Object.assign(this.osScrollBar.style, this.originalScrollBarStyles);
  }

  _calculateScrollBarStyles(enteredOrLeft = 'left') {
    /* Calculate the scroll bar styles based on the vertical container */
    let {
      width: scrollBarWidth,
      left: scrollBarLeft,
    } = this.verticalContainer.getBoundingClientRect();
    this.originalScrollBarStyles = {
      position: 'absolute',
      top: 'auto',
      bottom: 0,
      left: 0,
      width: scrollBarWidth + 'px'
    };

    this._setVerticalDetectorWidth();

    /* Dialogs doesn't have full height of the viewport,
       so we need to calculate the top position of the scroll bar
       based on the dialog bottom position if they have fixed position */
    if (this.isDialog) {
      const dialog = this.verticalContainer.closest('dialog');

      /* Reset scrollbar width, needed on viewport resize */
      this.osScrollBar.style.width = this.verticalContainer.offsetWidth + 'px';
      this.originalScrollBarStyles.width = this.verticalContainer.offsetWidth + 'px';

      if (dialog?.open && enteredOrLeft === 'left') {
        const dialogBottom = dialog.getBoundingClientRect().bottom;
        const scrollBarHeight = this.osScrollBar.offsetHeight;
        this.fixedScrollBarStyles.top = dialogBottom - scrollBarHeight + 'px';
      }
    }
    this.fixedScrollBarStyles.width = scrollBarWidth + 'px';
    this.fixedScrollBarStyles.left = scrollBarLeft + 'px';
  }

  /* Sometimes the layout structure is too complex
     for the verticalDetector to have the necessary width.
     So we need to set the width of the verticalDetector
     according to the widest element inside the verticalContainer. */
  _setVerticalDetectorWidth() {
    const allChildren = Array.from(this.verticalContainer.querySelectorAll('*:not([data-horizontal-scroll-target="verticalDetector"]):not(.os-scrollbar-horizontal):not(.os-scrollbar-track):not(.os-scrollbar-handle)'));
    const widestElementWidth = Math.max(...allChildren.map((element) => element.getBoundingClientRect().width));
    this.verticalDetector.style.width = widestElementWidth + 'px';
  }

  /* Sometimes we need a direct descendant of an element because an element
     may contain multiple nested descendants with the same attribute. */
  _findDirectChild(element, matchingElement) {
    /* Find all direct children of the element and create array from this nodeList
       so we can use find method */
    const children = Array.from(element.children);
    /* Return the first child that matches the matchingElement selector */
    return children.find(child => child.matches(matchingElement));
  }
}